"use client"

import React, { useState } from "react"
import { useTranslations } from "next-intl"
import { LayoutGrid, ChevronDown, ChevronRight } from "lucide-react"

import { useSearchParams } from "next/navigation"
import { Link, usePathname } from "@/lib/i18n/navigation"
import { cn } from "@/lib/utils"
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"

import type { Category } from "@/lib/graphql/types"

interface CategorySidebarProps {
  categories: Category[]
  /** The current active category slug. Undefined on the Shop All page. */
  currentSlug?: string
}

/**
 * Determines the initial open state for each parent category.
 * A parent is expanded by default. All parents with children start expanded.
 */
function buildInitialExpandedState(categories: Category[]): Record<string, boolean> {
  const state: Record<string, boolean> = {}
  for (const cat of categories) {
    if (cat.children.length > 0) {
      state[cat.id] = true
    }
  }
  return state
}

function CategoryTree({
  categories,
  currentSlug,
  onLinkClick,
}: {
  categories: Category[]
  currentSlug?: string
  onLinkClick?: () => void
}) {
  const t = useTranslations("categorySidebar")
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const isOnProductsPage = pathname.endsWith("/products") && !currentSlug
  const isOnNewArrivals = isOnProductsPage && searchParams.get("new") === "1"
  const isOnShopAll = isOnProductsPage && !isOnNewArrivals

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    buildInitialExpandedState(categories),
  )

  const toggleCategory = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const isActiveCategory = (slug: string) => slug === currentSlug

  return (
    <ul className="space-y-0.5">
      <li>
        <Link
          href="/products"
          onClick={onLinkClick}
          className={cn(
            "block py-2 text-sm motion-safe:transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded",
            isOnShopAll ? "font-semibold text-primary" : "text-foreground",
          )}
        >
          {t("allProducts")}
        </Link>
      </li>
      <li>
        <Link
          href="/products?new=1"
          onClick={onLinkClick}
          className={cn(
            "block py-2 text-sm motion-safe:transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded",
            isOnNewArrivals ? "font-semibold text-primary" : "text-foreground",
          )}
        >
          {t("newArrivals")}
        </Link>
      </li>

      {categories.map((category) => {
        const hasChildren = category.children.length > 0
        const isActive = isActiveCategory(category.slug)
        const isExpanded = hasChildren ? (expanded[category.id] ?? true) : false

        return (
          <li key={category.id}>
            {hasChildren ? (
              <>
                {/* Parent category with children */}
                <div className="flex items-center">
                  <Link
                    href={`/category/${category.slug}`}
                    onClick={onLinkClick}
                    className={cn(
                      "flex-1 py-2 text-sm motion-safe:transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded truncate",
                      isActive ? "font-semibold text-primary" : "text-foreground",
                    )}
                  >
                    {category.name}
                  </Link>
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-label={
                      isExpanded
                        ? t("collapseCategory", { name: category.name })
                        : t("expandCategory", { name: category.name })
                    }
                    onClick={() => toggleCategory(category.id)}
                    className="shrink-0 flex items-center justify-center min-h-11 px-2 text-muted-foreground motion-safe:transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  >
                    {isExpanded ? (
                      <ChevronDown className="size-3.5" />
                    ) : (
                      <ChevronRight className="size-3.5" />
                    )}
                  </button>
                </div>

                {/* Children list */}
                {isExpanded && (
                  <ul className="ml-3 border-l border-border">
                    {category.children.map((child) => {
                      const isChildActive = isActiveCategory(child.slug)
                      return (
                        <li key={child.id}>
                          <Link
                            href={`/category/${child.slug}`}
                            onClick={onLinkClick}
                            className={cn(
                              "block py-1.5 pl-3 text-sm motion-safe:transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded truncate",
                              isChildActive
                                ? "font-semibold text-primary"
                                : "text-muted-foreground",
                            )}
                          >
                            {child.name}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </>
            ) : (
              <Link
                href={`/category/${category.slug}`}
                onClick={onLinkClick}
                className={cn(
                  "block py-2 text-sm motion-safe:transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded truncate",
                  isActive ? "font-semibold text-primary" : "text-foreground",
                )}
              >
                {category.name}
              </Link>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * CategorySidebar — "use client" component.
 *
 * Desktop (>= lg): renders a persistent left-side <nav> with the full category tree.
 * Mobile (< lg): renders a Sheet/drawer trigger button. Tapping opens a left-side
 * sheet with the same category tree content.
 *
 * Props:
 * - categories: Category[] — from getCategories() (parent + children structure)
 * - currentSlug?: string — current active category slug (undefined on Shop All page)
 */
export function CategorySidebar({ categories, currentSlug }: CategorySidebarProps) {
  const t = useTranslations("categorySidebar")
  const [sheetOpen, setSheetOpen] = useState(false)

  const handleLinkClick = () => {
    setSheetOpen(false)
  }

  return (
    <>
      {/* ── Desktop sidebar (hidden on mobile) ── */}
      <nav aria-label={t("title")} className="hidden lg:block w-56 shrink-0">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("title")}
        </p>
        <CategoryTree categories={categories} currentSlug={currentSlug} />
      </nav>

      {/* ── Mobile Sheet trigger (visible only on mobile) ── */}
      <div className="lg:hidden">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger
            render={
              <button
                type="button"
                aria-label={t("openCategories")}
                className="inline-flex min-h-11 min-w-11 items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium motion-safe:transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            }
          >
            <LayoutGrid className="size-4" aria-hidden="true" />
            <span className="sr-only sm:not-sr-only">{t("title")}</span>
          </SheetTrigger>
          <SheetContent side="left" closeLabel={t("title")}>
            <SheetHeader>
              <SheetTitle>{t("title")}</SheetTitle>
            </SheetHeader>
            <div className="px-4 py-2 overflow-y-auto">
              <CategoryTree
                categories={categories}
                currentSlug={currentSlug}
                onLinkClick={handleLinkClick}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  )
}
